
import React, { useState, useEffect } from 'react';
import {
  Box, IconButton, Paper, Typography, Menu, MenuItem,
  ListItemIcon, ListItemText, Drawer, Avatar, Divider, Badge
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Groups, Inventory, PrecisionManufacturing, AccountBalance,
  ViewKanban, LocalShipping, Timer, Inventory2, Warehouse,
  People, Business, Storefront, ShoppingCart,
  Category, Sell, AutoAwesomeMotion, CalendarMonth, Close as CloseIcon, Dashboard,
  Add as AddIcon, Edit as EditIcon, Save as SaveIcon, UploadFile,
  ChevronRight as ChevronRightIcon,
  Mic as MicIcon,
  Send as SendIcon,
  Description as DescriptionIcon,
  Bolt as BoltIcon,
  Foundation as FoundationIcon,
  Terminal as TerminalIcon,
  Gavel as GavelIcon,
  Chat as ChatIcon,
  SmartToy as SmartToyIcon,
  Assignment as AssignmentIcon,
  ConfirmationNumber as EventIcon,
  Public as PublicIcon,
  Assessment as AssessmentIcon,
  BarChart as BarChartIcon
} from '@mui/icons-material';
import { API_BASE_URL } from '../config/api';



const MobileBottomNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeRadial, setActiveRadial] = useState(null); // 'verwaltung', 'sortiment', 'produktion', 'finanzen', 'aufgaben'
  const [companyIsEditing, setCompanyIsEditing] = useState(false);
  const [isMicListening, setIsMicListening] = useState(false);
  const [overdueCount, setOverdueCount] = useState(0);

  useEffect(() => {
    fetch(`${API_BASE_URL}/api/kanban/overdue-count`)
      .then(r => r.json())
      .then(d => setOverdueCount(d.overdueCount || 0))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const handleStatus = (e) => setCompanyIsEditing(e.detail.isEditing);
    const handleMicState = (e) => setIsMicListening(e.detail.listening);
    const handleOpenProduktion = () => setActiveRadial('produktion');
    window.addEventListener('company-status-change', handleStatus);
    window.addEventListener('mic-state-change', handleMicState);
    window.addEventListener('open-produktion-drawer', handleOpenProduktion);
    return () => {
      window.removeEventListener('company-status-change', handleStatus);
      window.removeEventListener('mic-state-change', handleMicState);
      window.removeEventListener('open-produktion-drawer', handleOpenProduktion);
    };
  }, []);

  const radialConfigs = {
    verwaltung: {
      color: '#ef4444',
      icon: <Groups />,
      items: [
        { label: 'Mitarbeiter', icon: <People />, route: '/verwaltung/mitarbeiter' },
        { label: 'Personal', icon: <Inventory2 />, route: '/verwaltung/personal' },
        { label: 'Abteilung', icon: <Business />, route: '/verwaltung/abteilungen' },
        { label: 'Kunden', icon: <Storefront />, route: '/verwaltung/kunden' },
        { label: 'Dokumente', icon: <DescriptionIcon />, route: '/allgemein/dokumente' },
        { label: 'Workflows', icon: <BoltIcon />, route: '/allgemein/workflows' },
        { label: 'Firma', icon: <FoundationIcon />, route: '/allgemein/firma' },
        { label: 'IT', icon: <TerminalIcon />, route: '/allgemein/it' },
        { label: 'Recht', icon: <GavelIcon />, route: '/allgemein/recht' },
      ],
      angles: [100, 70, 40],
      posX: '18%'
    },
    sortiment: {
      color: '#8b5cf6',
      icon: <Inventory />,
      items: [
        { label: 'Einkauf', icon: <ShoppingCart />, route: '/sortiment/einkauf' },
        { label: 'Produkte', icon: <Category />, route: '/sortiment/produkte' },
        { label: 'Preise', icon: <Sell />, route: '/sortiment/preise' },
        { label: 'Bundles', icon: <AutoAwesomeMotion />, route: '/sortiment/bundles' },
      ],
      angles: [110, 85, 60, 35],
      posX: '34%'
    },
    produktion: {
      color: '#3b82f6',
      icon: <PrecisionManufacturing />,
      items: [
        { label: 'MES', icon: <PrecisionManufacturing />, action: 'mes_submenu' },
        { label: 'Kapazitätsplanung', icon: <Timer />, route: '/produktion/kapazitaetsplanung' },
      ],
      angles: [110, 70],
      posX: '50%'
    },
    mes_submenu: {
      color: '#3b82f6',
      icon: <PrecisionManufacturing />,
      items: [
        { label: 'Shopfloor', icon: <PrecisionManufacturing />, route: '/produktion/mes-fertigung' },
        { label: 'Produktion-Flow', icon: <ViewKanban />, route: '/produktion/mes-fertigung#list' },
        { label: 'Qualitätskontrolle', icon: <AssignmentIcon />, route: '/produktion/qualitaetskontrolle' },
      ],
      angles: [130, 90, 50],
      posX: '50%'
    },
    finanzen: {
      color: '#06b6d4',
      icon: <AccountBalance />,
      items: [
        { label: 'Debitoren', icon: <AccountBalance />, route: '/crm-sales/debitoren' },
        { label: 'Ausgaben', icon: <AccountBalance />, route: '/finanzen-controlling/ausgaben' },
        { label: 'Aufträge / Sales', icon: <AssignmentIcon />, route: '/crm-sales/auftraege' },
        { label: 'BI & Analyse', icon: <BarChartIcon />, route: '/bi-analytics' },
        { label: 'Märkte & Events', icon: <EventIcon />, route: '/crm-sales/maerkte' },
        { label: 'Woocommerce', icon: <PublicIcon />, route: '/crm-sales/woocommerce' },
        { label: 'Finanzen', icon: <AssessmentIcon />, route: '/finanzen-controlling/overview' },
        { label: 'Datev Management', icon: <AccountBalance />, route: '/finanzen-controlling/datev' },
      ],
      angles: [140, 110, 80],
      posX: '66%'
    },
    aufgaben: {
      color: '#d946ef',
      icon: <ViewKanban />,
      items: [
        { label: 'Kanban', icon: <ViewKanban />, route: '/kanban-aufgaben/kanban' },
        { label: 'Kalender', icon: <CalendarMonth />, route: '/kanban-aufgaben/kalender' },
        { label: 'Whiteboard', icon: <Dashboard />, route: '/whiteboard' },
        { label: 'Checklisten', icon: <AssignmentIcon />, route: '/checklisten' },
        { divider: true },
        { label: 'Chatroom', icon: <ChatIcon />, route: '/nachrichten/chatroom' },
        { label: 'Chatbot', icon: <SmartToyIcon />, route: '/nachrichten/ai-bot' },
      ],
      angles: [140, 110],
      posX: '82%'
    }
  };

  const handleToggleRadial = (tab) => {
    setActiveRadial(activeRadial === tab ? null : tab);
  };

  const handleFABClick = () => {
    if (activeRadial === 'produktion') {
      setActiveRadial(null);
    } else if (location.pathname.includes('/produktion/mes-fertigung') || location.pathname.includes('/produktion-logistik/produktion')) {
      window.dispatchEvent(new CustomEvent('produktion-open-create'));
    } else if (location.pathname.includes('/kanban-aufgaben/kanban')) {
      window.dispatchEvent(new CustomEvent('kanban-open-create'));
    } else if (location.pathname.includes('/kanban-aufgaben/kalender')) {
      window.dispatchEvent(new CustomEvent('kalender-open-create'));
    } else if (location.pathname.includes('/verwaltung/mitarbeiter')) {
      window.dispatchEvent(new CustomEvent('mitarbeiter-open-create'));
    } else if (location.pathname.includes('/allgemein/workflows')) {
      window.dispatchEvent(new CustomEvent('workflow-open-create'));
    } else if (location.pathname.includes('/allgemein/firma')) {
      window.dispatchEvent(new CustomEvent('company-action'));
    } else if (location.pathname.includes('/allgemein/dokumente')) {
      window.dispatchEvent(new CustomEvent('documents-open-upload'));
    } else if (location.pathname.includes('/allgemein/recht')) {
      window.dispatchEvent(new CustomEvent('legal-open-upload'));
    } else if (location.pathname.includes('/verwaltung/personal')) {
      window.dispatchEvent(new CustomEvent('hr-open-upload'));
    } else if (location.pathname.includes('/verwaltung/abteilungen')) {
      window.dispatchEvent(new CustomEvent('abteilungen-open-create'));
    } else if (location.pathname.includes('versand')) {
      if (typeof window.openVersandErfassung === 'function') {
        window.openVersandErfassung();
      } else {
        const event = new CustomEvent('versand-open-create');
        window.dispatchEvent(event);
        document.dispatchEvent(event);
      }
    } else if (location.pathname.includes('arbeitszeiten')) {
      const event = new CustomEvent('working-hours-open-create');
      window.dispatchEvent(event);
      document.dispatchEvent(event);
    } else if (location.pathname.includes('/verwaltung/kunden')) {
      window.dispatchEvent(new CustomEvent('kunden-open-create'));
    } else if (location.pathname.includes('/finanzen-controlling/ausgaben')) {
      const event = new CustomEvent('open-create-ausgabe', { bubbles: true, composed: true });
      window.dispatchEvent(event);
      document.dispatchEvent(event);
    } else if (location.pathname.includes('/sortiment/preise')) {
      window.dispatchEvent(new CustomEvent('preise-open-create', { bubbles: true, composed: true }));
    } else if (location.pathname.includes('/kanban-aufgaben/whiteboard')) {
      window.dispatchEvent(new CustomEvent('whiteboard-open-create', { bubbles: true, composed: true }));
    } else if (location.pathname.includes('/crm-sales/debitoren')) {
      window.dispatchEvent(new CustomEvent('debitoren-open-create'));
    } else if (location.pathname.includes('/crm-sales/maerkte')) {
      window.dispatchEvent(new CustomEvent('markt-open-create'));
    } else if (location.pathname.includes('/sortiment/produkte')) {
      window.dispatchEvent(new CustomEvent('produkte-open-create'));
    } else if (location.pathname.includes('/sortiment/einkauf')) {
      const event = new CustomEvent('einkauf-open-create');
      window.dispatchEvent(event);
      document.dispatchEvent(event);
    } else if (location.pathname.includes('/sortiment/bundles')) {
      const event = new CustomEvent('bundles-open-create');
      window.dispatchEvent(event);
      document.dispatchEvent(event);
    } else if (location.pathname.includes('/checklisten')) {
      window.dispatchEvent(new CustomEvent('checklisten-open-create'));
    } else {
      handleToggleRadial('produktion');
    }
  };

  const handleNavigate = (item) => {
    if (item.action === 'mes_submenu') {
      setActiveRadial('mes_submenu');
    } else if (item.route) {
      navigate(item.route);
      setActiveRadial(null);
    }
  };

  return (
    <>
      {/* Sub-Menu Drawer */}
      <Drawer
        anchor="bottom"
        open={Boolean(activeRadial)}
        onClose={() => setActiveRadial(null)}
        variant="temporary"
        ModalProps={{ keepMounted: true }}
        PaperProps={{
          sx: {
            height: 'calc(100dvh - 80px)',
            borderTopLeftRadius: 32,
            borderTopRightRadius: 32,
            bgcolor: '#fff',
            backgroundImage: 'none',
            boxShadow: 'none',
            mb: '80px', // Docked exactly above navbar
            width: '100%',
            bottom: 0,
            overflow: 'hidden',
            border: 'none',
            borderTop: '1px solid #f1f5f9'
          }
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* Menu Header */}
          <Box sx={{
            px: 2.5, py: 2, bgcolor: '#fff', borderBottom: '1px solid #f1f5f9',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0
          }}>
            <Typography variant="caption" sx={{ fontWeight: 900, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 1.5 }}>
              {activeRadial === 'verwaltung' ? 'Verwaltung' :
                activeRadial === 'sortiment' ? 'Sortiment' :
                  activeRadial === 'produktion' ? 'Datenerfassung' :
                    activeRadial === 'mes_submenu' ? 'MES' :
                      activeRadial === 'finanzen' ? 'Finanzen' :
                        activeRadial === 'aufgaben' ? 'Aufgaben' : 'Menü'}
            </Typography>
            <IconButton size="small" onClick={() => setActiveRadial(null)} sx={{ color: '#94a3b8' }}>
              <CloseIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Box>

          <Box sx={{ p: 2, overflowY: 'auto', flex: 1, bgcolor: '#f8fafc' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2 }}>
              {activeRadial && radialConfigs[activeRadial].items.map((item, idx) => (
                item.divider ? (
                  <Divider key={idx} textAlign="left" sx={{ my: 2, '&::before': { display: 'none' }, '&::after': { borderColor: '#f1f5f9', borderBottomWidth: 2 } }}>
                    <Typography variant="caption" sx={{ fontWeight: 900, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 1.5 }}>
                      Weitere Module
                    </Typography>
                  </Divider>
                ) : (
                  <Paper
                    key={idx}
                    elevation={0}
                    onClick={() => handleNavigate(item)}
                    sx={{
                      p: 2.5, borderRadius: 5, border: '1px solid #f1f5f9', bgcolor: '#fff',
                      display: 'flex', alignItems: 'center', gap: 2.5, cursor: 'pointer',
                      transition: 'all 0.2s', '&:active': { transform: 'scale(0.98)', bgcolor: '#f8fafc' }
                    }}
                  >
                    <Avatar sx={{ bgcolor: radialConfigs[activeRadial].color + '15', color: radialConfigs[activeRadial].color, width: 52, height: 52, borderRadius: 4 }}>
                      {React.cloneElement(item.icon, { sx: { fontSize: 28 } })}
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ fontWeight: 800, color: '#1e293b', fontSize: '1rem' }}>{item.label}</Typography>
                      <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 600 }}>Modul öffnen</Typography>
                    </Box>
                    {/* Overdue badge for Kanban item */}
                    {item.label === 'Kanban' && overdueCount > 0 && (
                      <Box sx={{
                        bgcolor: '#fef2f2', color: '#ef4444', px: 1, py: 0.3, borderRadius: '8px',
                        fontSize: 11, fontWeight: 900, border: '1px solid #fecaca', mr: 1
                      }}>
                        {overdueCount}
                      </Box>
                    )}
                    <ChevronRightIcon sx={{ color: '#cbd5e1' }} />
                  </Paper>
                )
              ))}
            </Box>
          </Box>
        </Box>
      </Drawer>

      {/* Modern Fixed Navbar */}
      <Paper
        elevation={0}
        sx={{
          position: 'fixed', bottom: 0, left: 0, right: 0,
          width: '100%', height: 80,
          bgcolor: '#fff', borderRadius: '32px 32px 0 0',
          display: 'flex', justifyContent: 'space-around', alignItems: 'center',
          px: 1, zIndex: 1400,
          boxShadow: '0 -10px 30px rgba(0,0,0,0.06)',
          borderTop: '1px solid rgba(241, 245, 249, 0.8)',
          boxSizing: 'border-box',
          pb: 'env(safe-area-inset-bottom)' // Support for iPhone notch/home bar
        }}
      >
        {/* Verwaltung */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
          <IconButton
            onClick={() => handleToggleRadial('verwaltung')}
            sx={{ color: activeRadial === 'verwaltung' ? '#1e293b' : '#94a3b8', transition: 'all 0.3s' }}
          >
            <Groups sx={{ fontSize: 26 }} />
          </IconButton>
          <Typography sx={{ color: activeRadial === 'verwaltung' ? '#1e293b' : '#94a3b8', fontSize: '0.6rem', fontWeight: 800, mt: -0.5 }}>Verwaltung</Typography>
        </Box>

        {/* Sortiment */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
          <IconButton
            onClick={() => handleToggleRadial('sortiment')}
            sx={{ color: activeRadial === 'sortiment' ? '#1e293b' : '#94a3b8', transition: 'all 0.3s' }}
          >
            <Inventory sx={{ fontSize: 24 }} />
          </IconButton>
          <Typography sx={{ color: activeRadial === 'sortiment' ? '#1e293b' : '#94a3b8', fontSize: '0.6rem', fontWeight: 800, mt: -0.5 }}>Sortiment</Typography>
        </Box>

        {/* Central FAB */}
        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', position: 'relative', top: -20 }}>
          <Box sx={{
            p: '6px', borderRadius: '50%', bgcolor: '#fff',
            boxShadow: '0 8px 20px rgba(0,0,0,0.05)'
          }}>
            <IconButton
              onClick={handleFABClick}
              sx={{
                bgcolor: '#1e293b', color: '#fff',
                width: 60, height: 60,
                boxShadow: '0 8px 25px rgba(30, 41, 59, 0.4)',
                '&:active': { transform: 'scale(0.9)' },
                '&:hover': { bgcolor: '#0f172a' },
                transition: 'all 0.2s',
              }}
            >
              {activeRadial === 'produktion'
                ? <CloseIcon sx={{ fontSize: 32 }} />
                : ((location.pathname.includes('/produktion/mes-fertigung') ||
                  location.pathname.includes('/produktion-logistik/produktion') ||
                  location.pathname.includes('/kanban-aufgaben/kanban') ||
                  location.pathname.includes('/kanban-aufgaben/kalender') ||
                  location.pathname.includes('/verwaltung/mitarbeiter') ||
                  location.pathname.includes('/allgemein/workflows') ||
                  location.pathname.includes('/verwaltung/abteilungen') ||
                  location.pathname.includes('/sortiment/produkte') ||
                  location.pathname.includes('versand') ||
                  location.pathname.includes('arbeitszeiten') ||
                  location.pathname.includes('ausgaben') ||
                  location.pathname.includes('preise') ||
                  location.pathname.includes('whiteboard') ||
                  location.pathname.includes('/crm-sales/maerkte') ||
                  location.pathname.includes('/crm-sales/debitoren') ||
                  location.pathname.includes('/sortiment/einkauf') ||
                  location.pathname.includes('/sortiment/bundles') ||
                  location.pathname.includes('/checklisten') ||
                  location.pathname.includes('/verwaltung/kunden'))
                  ? <AddIcon sx={{ fontSize: 32 }} />
                  : (location.pathname.includes('/allgemein/firma')
                    ? (companyIsEditing ? <SaveIcon sx={{ fontSize: 32 }} /> : <EditIcon sx={{ fontSize: 32 }} />)
                    : ((location.pathname.includes('/allgemein/dokumente') ||
                      location.pathname.includes('/allgemein/recht') ||
                      location.pathname.includes('/verwaltung/personal'))
                      ? <UploadFile sx={{ fontSize: 32 }} />
                      : <PrecisionManufacturing sx={{ fontSize: 28 }} />
                    )
                  )
                )
              }
            </IconButton>
          </Box>
          <Typography sx={{
            color: '#94a3b8', fontSize: '0.6rem', fontWeight: 800,
            position: 'absolute', bottom: -15, whiteSpace: 'nowrap'
          }}>
            {location.pathname.includes('versand') ||
            location.pathname.includes('arbeitszeiten') ||
            location.pathname.includes('/verwaltung/kunden') ||
            location.pathname.includes('/produktion/mes-fertigung') ||
            location.pathname.includes('/produktion-logistik/produktion') ||
            location.pathname.includes('/kanban-aufgaben/kanban') ||
            location.pathname.includes('/kanban-aufgaben/kalender') ||
            location.pathname.includes('/verwaltung/mitarbeiter') ||
            location.pathname.includes('ausgaben') ||
            location.pathname.includes('preise') ||
            location.pathname.includes('whiteboard') ||
            location.pathname.includes('/crm-sales/maerkte') ||
            location.pathname.includes('/crm-sales/debitoren') ||
            location.pathname.includes('/sortiment/einkauf') ||
            location.pathname.includes('/sortiment/bundles') ||
            location.pathname.includes('/checklisten') ||
            location.pathname.includes('/verwaltung/abteilungen')
              ? 'Hinzufügen'
              : ((location.pathname.includes('/allgemein/dokumente') ||
                location.pathname.includes('/allgemein/recht') ||
                location.pathname.includes('/verwaltung/personal')) ? 'Upload' : 'Produktion')
            }
          </Typography>
        </Box>

        {/* Finanzen */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
          <IconButton
            onClick={() => handleToggleRadial('finanzen')}
            sx={{ color: activeRadial === 'finanzen' ? '#1e293b' : '#94a3b8', transition: 'all 0.3s' }}
          >
            <AccountBalance sx={{ fontSize: 24 }} />
          </IconButton>
          <Typography sx={{ color: activeRadial === 'finanzen' ? '#1e293b' : '#94a3b8', fontSize: '0.6rem', fontWeight: 800, mt: -0.5 }}>Finanzen</Typography>
        </Box>

        {/* Aufgaben */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, position: 'relative' }}>
          <IconButton
            onClick={() => handleToggleRadial('aufgaben')}
            sx={{ color: activeRadial === 'aufgaben' ? '#1e293b' : '#94a3b8', transition: 'all 0.3s' }}
          >
            <Badge badgeContent={overdueCount} color="error" sx={{ '& .MuiBadge-badge': { fontSize: 9, minWidth: 16, height: 16, fontWeight: 900 } }}>
              <ViewKanban sx={{ fontSize: 24 }} />
            </Badge>
          </IconButton>
          <Typography sx={{ color: activeRadial === 'aufgaben' ? '#1e293b' : '#94a3b8', fontSize: '0.6rem', fontWeight: 800, mt: -0.5 }}>Aufgaben</Typography>
        </Box>
      </Paper>
    </>
  );
};

export default MobileBottomNav;